import React, { useState } from 'react';
import { useAuth } from '../utils/AuthContext';
import Logo from '../components/ui/Logo';
import '../styles/Login.css';

/** Served from `public/` — avoid url() in CSS so webpack does not try to bundle it. */
const LOGIN_WATERMARK_URL = `${process.env.PUBLIC_URL || ''}/assets/logo/LCTF%20Profile.png`;

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, signInWithGoogle, requestPasswordReset } = useAuth();
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetLinkSending, setResetLinkSending] = useState(false);
  const [resetLinkBanner, setResetLinkBanner] = useState<'idle' | 'sent' | 'error'>('idle');
  const [resetLinkError, setResetLinkError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      await login(email, password);
    } catch (error: any) {
      setErrorMessage(error.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      await signInWithGoogle();
    } catch (error: any) {
      setErrorMessage(error.message || 'An error occurred');
      setIsLoading(false);
    }
  };

  const handleSendPasswordResetLink = async () => {
    setResetLinkBanner('idle');
    setResetLinkError('');
    const trimmed = email.trim();
    if (!trimmed) {
      setResetLinkError('Enter your email address above first.');
      setResetLinkBanner('error');
      return;
    }
    if (!trimmed.includes('@')) {
      setResetLinkError(
        'Username logins do not have an inbox. Ask an admin to reset the password from Settings → Team.',
      );
      setResetLinkBanner('error');
      return;
    }
    setResetLinkSending(true);
    try {
      await requestPasswordReset(trimmed);
      setResetLinkBanner('sent');
    } catch (error: unknown) {
      const err = error as { message?: string; code?: string };
      setResetLinkError(err?.message || 'Could not send reset email. Try again later.');
      setResetLinkBanner('error');
    } finally {
      setResetLinkSending(false);
    }
  };

  return (
    <div className="login-container">
      <div
        className="login-container-watermark"
        aria-hidden
        style={{ backgroundImage: `url("${LOGIN_WATERMARK_URL}")` }}
      />
      <div className="login-card">
        <div className="login-header">
          <div className="login-header-logo" aria-hidden>
            <Logo size={72} />
          </div>
          <h1>LCTF Client Portal</h1>
          <p>Timber Frame Construction Projects</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email or username</label>
            <input
              type="text"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="you@company.com or job-trailer"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <div className="login-forgot-row">
            <button
              type="button"
              className="login-forgot-link"
              disabled={isLoading}
              onClick={() => {
                setForgotPasswordOpen((o) => !o);
                setResetLinkBanner('idle');
                setResetLinkError('');
              }}
            >
              {forgotPasswordOpen ? 'Hide password reset' : 'Forgot password?'}
            </button>
          </div>

          {forgotPasswordOpen && (
            <div className="login-forgot-panel">
              <p style={{ margin: 0 }}>
                Email accounts get a reset link. Workstation usernames need an admin to reset the password
                from Settings → Team.
              </p>
              <div className="login-forgot-actions">
                <button
                  type="button"
                  className="login-button"
                  style={{ padding: '10px 18px', fontSize: 14 }}
                  disabled={isLoading || resetLinkSending}
                  onClick={handleSendPasswordResetLink}
                >
                  {resetLinkSending ? 'Sending…' : 'Send password reset link'}
                </button>
              </div>
              {resetLinkBanner === 'sent' && (
                <p className="login-success-message" style={{ marginTop: 12, marginBottom: 0 }} role="status">
                  If an account exists for that email, you will receive a reset link shortly. Check your spam folder if
                  nothing arrives in a few minutes.
                </p>
              )}
              {resetLinkBanner === 'error' && resetLinkError && (
                <div className="error-message" style={{ marginTop: 12 }}>
                  {resetLinkError}
                </div>
              )}
            </div>
          )}

          {errorMessage && (
            <div className="error-message">{errorMessage}</div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Please wait...' : 'Sign In'}
          </button>
        </form>

        <div className="divider">
          <span>or</span>
        </div>

        <button
          onClick={handleGoogleSignIn}
          className="google-button"
          disabled={isLoading}
        >
          <svg className="google-icon" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <p className="login-invite-hint">
          Need an account? Ask your Leiper&apos;s Creek Timber Frames administrator for an invite.
        </p>
      </div>
    </div>
  );
};

export default Login;
